import React, { useCallback, useEffect, useState } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { useForm, FormProvider } from 'react-hook-form';
import classNames from 'classnames';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';

import * as selectors from 'memberPortalModules/MemberProfile/state/invoice/selectors';

import { useAppDispatch } from 'store/hooks';

import { IInvoiceDetailsImt, INotifications, InvoiceStatus } from 'common/interfaces/invoices';

import inputLabels from 'common/messages/inputLabels';
import commonMessages from 'common/messages/messages';
import { ENotificationType } from 'modules/booking/constants/notificationType';
import { InvoiceStatuses } from 'common/constants/invoices';
import PrintKeysService from 'services/PrintKeyStorageService/PrintKeyStorageService';

import { ReactComponent as PrintIcon } from 'img/icons/printer_deprecated.svg';
import { Button, ButtonWithCircularProgress, DialogComponent } from 'common/components';
import CheckOutResponseNamesInfo from 'common/components/InvoiceOperating/InvoiceModals/CheckOutResponseModal/CheckOutResponseNamesInfo';
import CheckOutResponsePaymentInfo from 'common/components/InvoiceOperating/InvoiceModals/CheckOutResponseModal/CheckOutResponsePaymentInfo';
import { useCombinePrintLoading } from 'common/hooks/useCombinePrintLoading';
import useComponentDidUpdate from 'common/hooks/useComponentDidUpdate';
import { setPrinterModalState } from 'common/state/printHTML/actions';
import { selectSetPrinterModalState } from 'common/state/printHTML/selectors';
import { SavePrintKeysModal } from 'common/modals';
import {
  fetchReceipt,
  resetCheckoutResultData,
  resetMemberInvoice,
} from 'memberPortalModules/MemberProfile/state/invoice/actions';
import {
  fetchProfileInfoView,
  fetchSenderAvailabilityThunk,
} from 'memberPortalModules/MemberProfile/state/profile/actions';
import { SenderAvailabilityTypeList } from 'modules/booking/constants/senderAvailability';
import CheckOutResponsePaymentNotifyControl from './CheckOutResponsePaymentNotifyControl';
import { fetchPersonDocuments } from 'memberPortalModules/MemberProfile/state/documents/actions';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    overflow: 'hidden',
  },
  statusWrap: {
    display: 'inline-flex',
    minWidth: 24,
    height: 24,
    textAlign: 'center',
    padding: '3px 7px 3px 7px',
    borderRadius: 3,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.paper,
    fontSize: theme.typography.h5.fontSize,
    marginLeft: theme.spacing(1),
  },
  pending: {
    backgroundColor: theme.palette.warning.main,
  },
  printIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
  },
}));

interface ICheckOutNotificationResponse {
  checkoutData: IInvoiceDetailsImt;
  notificationData: INotifications;
}

interface ICheckOutResponseModal {
  onSubmit: (value: ICheckOutNotificationResponse) => void;
  checkOutResponse: IInvoiceDetailsImt;
}

const CheckOutResponseModal = ({
  onSubmit,
  checkOutResponse,
}: ICheckOutResponseModal): JSX.Element => {
  const classes = useStyles();

  // global state
  const dispatch = useAppDispatch();

  const isCheckOutLoading: boolean = useRootSelector(selectors.selectUpdatedInvoiceLoading);
  const isReceiptLoading: boolean = useRootSelector(selectors.selectReceiptLoading);
  const isUpdateInvoiceLoading = useRootSelector(selectors.selectIsUpdateInvoiceLoading);
  const { isOpenSetPrinterModal, printSessionExists } = useRootSelector(selectSetPrinterModalState);

  const [isOpenModal, setIsOpenModal] = useState(false);
  // form

  const formMethods = useForm<INotifications>({
    defaultValues: {
      print: false,
      sendByEmail: false,
      sendByText: false,
      sendNotification: false,
    },
    mode: 'onBlur',
  });

  const { handleSubmit, reset, getValues } = formMethods;

  const handlePrintReceipt = useCallback(
    (isSilentPrint?: boolean) => {
      dispatch(fetchReceipt({ invoiceId: checkOutResponse.get('id'), isSilentPrint }));
    },
    [dispatch, checkOutResponse],
  );

  const onSubmitModal = (values: INotifications) => {
    const data = { ...values, print: values.print || false };

    const resultData: ICheckOutNotificationResponse = {
      checkoutData: checkOutResponse,
      notificationData: data,
    };
    if (data.print) {
      if (PrintKeysService.getPrivateKey() && PrintKeysService.getDigitalCertificateKey()) {
        dispatch(setPrinterModalState({ isOpenSetPrinterModal: true }));
      } else {
        setIsOpenModal(true);
      }
    } else {
      onSubmit(resultData);
    }
  };

  const isPrinting = useCombinePrintLoading(isReceiptLoading);

  useEffect(() => {
    reset({
      print: false,
      sendByEmail: false,
      sendByText: false,
      sendNotification: false,
    });
  }, [reset]);

  useEffect(() => {
    dispatch(
      fetchSenderAvailabilityThunk([SenderAvailabilityTypeList.POS], {
        events: [ENotificationType.FRONTDESK_POS_RECEIPT_SENDING],
      }),
    );
  }, [dispatch]);

  useComponentDidUpdate(() => {
    if (!isOpenSetPrinterModal && printSessionExists) {
      const data: INotifications = getValues();
      const resultData: ICheckOutNotificationResponse = {
        checkoutData: checkOutResponse,
        notificationData: data,
      };

      dispatch(setPrinterModalState({ printSessionExists: false }));
      handlePrintReceipt(true);
      onSubmit(resultData);
    }
  }, [isOpenSetPrinterModal, printSessionExists, getValues, checkOutResponse, onSubmit]);

  useComponentDidUpdate(() => {
    if (
      !isOpenModal &&
      PrintKeysService.getPrivateKey() &&
      PrintKeysService.getDigitalCertificateKey()
    ) {
      dispatch(setPrinterModalState({ isOpenSetPrinterModal: true }));
    }
  }, [isOpenModal, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetCheckoutResultData());
      dispatch(resetMemberInvoice());
      dispatch(fetchProfileInfoView());
      dispatch(fetchPersonDocuments());
    };
  }, [dispatch]);

  return (
    <DialogComponent
      size="xs"
      title={
        <Box display="flex" alignItems="center">
          {checkOutResponse.size ? `Invoice #${checkOutResponse.get('number')}` : ''}
          <Box
            className={classNames(
              classes.statusWrap,
              checkOutResponse.get('status') === InvoiceStatus.PAID ? '' : classes.pending,
            )}
          >
            <Typography color="inherit">
              {InvoiceStatuses.translate(checkOutResponse.get('status'))}
            </Typography>
          </Box>
        </Box>
      }
      submitBtnTitle="Ok"
      isOpen={checkOutResponse.size > 0}
      disableFullScreenBehavior
      cancelBtn={false}
      submitBtn={false}
      onClose={() => {}}
      additionalActionButtons={[
        <Button
          variant="outlined"
          fullWidth
          disabled={isPrinting}
          color="primary"
          startIcon={<PrintIcon className={classes.printIcon} />}
          type="button"
          onClick={() => handlePrintReceipt()}
        >
          <FormattedMessage {...inputLabels.printReceipt} />
        </Button>,
        <ButtonWithCircularProgress
          // TODO set autoFocus={true} when material will be V5
          autoFocus={false}
          fullWidth
          type="submit"
          disabled={isPrinting}
          isSubmitting={isCheckOutLoading || isUpdateInvoiceLoading}
          onClick={handleSubmit(onSubmitModal)}
        >
          <FormattedMessage {...commonMessages.okBtn} />
        </ButtonWithCircularProgress>,
      ]}
      closeCross={false}
    >
      <FormProvider {...formMethods}>
        <form>
          <Box className={classes.wrapper} pb={3}>
            {checkOutResponse.size > 0 && (
              <>
                <CheckOutResponseNamesInfo checkOutData={checkOutResponse} />
                <CheckOutResponsePaymentInfo checkOutData={checkOutResponse} />
                <CheckOutResponsePaymentNotifyControl />
              </>
            )}
          </Box>
        </form>
      </FormProvider>
      {isOpenModal && <SavePrintKeysModal isOpen setIsOpenModal={setIsOpenModal} />}
    </DialogComponent>
  );
};

export default CheckOutResponseModal;
